import React, { useState, useEffect } from "react";
import styled, { css } from "styled-components";
import { useNavigate, useParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import * as yup from "yup";
import { useSnackbar } from "notistack";
import { getLocale } from "react-i18nify";

import {
  Grid,
  Typography,
  IconButton,
  Card,
  CardContent,
  Autocomplete,
  Box,
  FormHelperText,
  Stack,
  createFilterOptions,
} from "@mui/material";

import SaveIcon from "@mui/icons-material/Save";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import CloseIcon from "@mui/icons-material/Close";
import AddPhotoAlternateIcon from "@mui/icons-material/AddPhotoAlternate";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import InsertPhotoIcon from "@mui/icons-material/InsertPhoto";

import {
  createCollection,
  getCollectionById,
  updateCollection,
} from "src/api/collection";
import { searchProduct } from "src/api/product";

import device from "src/styles/device";
import { Black, Grey, Primary } from "src/styles/color";
import { checkNotEmpty, convertPrice, formatFromElastic } from "src/common";
import { validFileExtensions } from "src/common/file";
import { useLoading } from "src/context/LoadingContext";
import useYupValidationResolver from "src/hooks/useYupValidationResolver";

import Main from "src/components/ui/Container";
import Button from "src/components/core/Button";
import TextField from "src/components/core/TextField";
import Loading from "src/components/ui/Loading";
import UploadPdf from "src/components/upload/dropzone/UploadPdf";
import UploadImage from "src/components/upload/dropzone/UploadImage";
import SpottableImage from "src/components/HotspotImage/SpottableImage";

// Styled Components
const Container = styled.div`
  width: 100%;
  max-width: 1240px;
  position: relative;
  padding: 20px 20px 40px;
  margin: 0 auto;

  & > div > p {
    word-wrap: break-word;
  }

  @media ${device.tabletL} {
    padding: 20px 20px 36px;
  }
  @media ${device.mobileL} {
    padding: 16px 16px 24px;
  }
`;

const DivBackground = styled.div`
  width: 100%;
  padding: 24px;
  background-color: #ffffff;
  /* box-shadow: 0px 2px 10px rgba(0, 0, 0, 0.2); */
  border-radius: 16px;
  /* margin: 16px 0; */

  @media ${device.tabletL} {
    padding: 16px;
  }
`;

const SectionTitle = styled.div`
  font-size: 1rem;
  font-weight: 600;
  color: #252525;
  line-height: 1.2;

  span {
    color: #e04132;
    font-weight: 600;
  }

  @media ${device.mobileL} {
    font-size: 1rem;
  }
`;

const UploadBox = styled.div`
  border: 2px dashed #d0d0d4;
  border-radius: 16px;
  padding: 40px 24px;
  text-align: center;
  background: #fbfbfb;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  position: relative;
  width: 100%;
  aspect-ratio: ${({ aspectRatio }) => (aspectRatio ? aspectRatio : 1)};
  /* min-height: 350px; */

  &:hover {
    border-color: #e04132;
    background: #f9f9f9;
  }

  @media ${device.mobileL} {
    padding: 32px 16px;
    min-height: 160px;
  }
`;

const PreviewImage = styled.img`
  width: 100%;
  aspect-ratio: 1; /* 1440x540 pixel ratio */
  object-fit: cover;
  display: block;

  @media ${device.mobileL} {
    height: 160px;
  }
`;

const UploadedPreview = styled.div`
  position: relative;
  border-radius: 12px;
  overflow: hidden;
  background: #f5f5f5;
  border: 1px solid #e0e0e0;
  width: 100%;
`;

const CoverPreviewImage = styled(PreviewImage)`
  aspect-ratio: 1440 / 540; /* 1440x540 pixel ratio */
`;

const ImageEmpty = styled.div`
  align-items: center;
  display: flex;
  justify-content: center;
  width: 80px;
  height: 80px;
  background: #f2f1f3;
  border-radius: 12px;
  object-fit: cover;
  object-position: center;
  cursor: pointer;
  vertical-align: initial;
  margin-right: 8px;
  &:hover {
    opacity: 0.8;
  }

  &.active {
    border: 2px solid #707070;
  }
`;

const ProductChoiceDetail = styled.div`
  width: 100%;
  display: grid;
  grid-template-rows: auto;
  grid-template-columns: 35% 35% 30%;
  align-items: center;
  padding: 5px 0;
`;

const SpanProductDetail = styled.span`
  color: ${({ nameProd }) => (nameProd ? "#252525" : "#6C6C70")};
  font-size: 1rem;
  font-weight: 400;
  text-align: ${({ center }) => (center ? "center" : "left")};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  ${({ price }) =>
    price &&
    css`
      color: #e13b30;
      font-size: 1.25rem;
      font-weight: 600;

      align-self: flex-end;
      text-align: right;
      grid-column: 3;
      grid-row: 1 / span 2;
    `}
`;

const RemoveButton = styled(IconButton)`
  position: absolute !important;
  top: 10px;
  right: 10px;
  background: rgba(255, 255, 255, 0.9) !important;
  color: #e04132;
  width: 32px;
  height: 32px;
  box-shadow: 0px 2px 6px rgba(0, 0, 0, 0.2);

  &:hover {
    background: rgba(255, 255, 255, 1) !important;
    color: #c73529;
  }
`;

const UploadText = styled(Typography)`
  color: #666666;
  margin-top: 16px;
  text-align: center;
  font-weight: 500;
  font-size: 0.875rem !important;

  @media ${device.mobileL} {
    margin-top: 12px;
  }
`;

const UploadHelper = styled(Typography)`
  color: #999999;
  font-size: 0.75rem;
  margin-top: 16px;
  text-align: center;
  line-height: 1.4;
`;

const ProductCard = styled(Card)`
  &.MuiCard-root {
    border-radius: 12px;
    border: 1px solid #e0e0e0;
    transition: all 0.2s ease;
  }
  & .MuiCardContent-root,
  & .MuiCardContent-root:last-child {
    padding: 16px;
  }

  &:hover {
    border-color: #e04132;
    box-shadow: 0px 4px 12px rgba(224, 65, 50, 0.1);
  }
`;

const ButtonGroup = styled(Stack)`
  width: 100%;
  max-width: 1240px;
  height: 72px;
  z-index: 15;
  margin-top: 24px;

  /* @media ${device.tabletL} {
    position: fixed;
    bottom: 0;
    width: -webkit-fill-available;
    background-color: var(--white-color);
    min-height: 110px;
    padding: 32px 20px;
    border-radius: 24px 24px 0 0;
    box-shadow: 0px -2px 10px 0px rgba(0, 0, 0, 0.1);

    .MuiButton-root {
      padding: 8px 24px;
    }
  } */
`;

// Validation Schema
const collectionSchema = yup.object({
  name: yup.string().required("กรุณากรอกชื่อคอลเล็กชัน"),
  theme_color: yup
    .string()
    .required("กรุณากรอกธีมสี")
    .matches(
      /^#([0-9a-f]{3}|[0-9a-f]{6})$/i,
      "ควรเป็นรหัสสีฐานสิบหกที่ถูกต้อง (เช่น #FFF หรือ #000000)",
    ),
  description_th: yup.string().required("กรุณากรอกรายละเอียดภาษาไทย"),
  description_en: yup.string().required("กรุณากรอกรายละเอียดภาษาอังกฤษ"),
  display_image: yup.mixed().required("กรุณาอัปโหลดรูปปกคอลเล็กชัน"),
  close_up_image: yup.mixed().required("กรุณาอัปโหลด/เลือกรูปภาพใกล้สินค้า"),
  // collection_image: yup.mixed(),
  // ! ----------- image -----------
  // hotspot_first_image: yup.mixed(),
  // hotspot_second_image: yup.mixed(),
  // hotspot_third_image: yup.mixed(),
  // hotspot_fourth_image: yup.mixed(),
  // hotspot_fifth_image: yup.mixed(),
  // hotspot_sixth_image: yup.mixed(),
  pdf_url: yup.mixed().nullable(),
  product_choice_collections: yup.array().of(yup.object()).min(1).required(),
  // product_choice_hotspots: yup.array().of(
  //   yup.object({
  //     img_ref: yup.number(),
  //     top: yup.string(),
  //     left: yup.string(),
  //     product_choice_hotspot: yup.object(),
  //   }),
  // ),
  hotspot_images: yup
    .array()
    .of(
      yup.object({
        ref_index: yup.number().required(),
        image: yup.mixed().required(),
        hotspot: yup
          .array()
          .of(
            yup.object({
              pc_id: yup.object().required(),
              position_x: yup.number().required(),
              position_y: yup.number().required(),
            }),
          )
          .test({
            name: "hotspot-required",
            message: "จุด hotspot ต้องไม่ว่างเปล่า",
            test: (array) => {
              return array.every(({ pc_id }, index) => {
                return checkNotEmpty(pc_id);
              });
            },
          }),
      }),
    )
    .test({
      name: "required",
      message: "คอลเล็กชันต้องมีจุด hotspot อย่างน้อย 1 จุด",
      test: (array, context) => {
        let allHotspot = [];
        // eslint-disable-next-line array-callback-return
        array.map(({ hotspot }, index) => {
          allHotspot = allHotspot.concat(hotspot);
        });
        return allHotspot.length > 0;
      },
    })
    .test({
      name: "required",
      message: "กรุณาอัปโหลดรูปภาพในคอลเล็กชันเพิ่มเติมให้ครบถ้วน",
      test: (array) =>
        array.every(({ image }, index) => {
          return !!image;
        }),
    }),
});

const CollectionForm = ({ open }) => {
  let { collectionId } = useParams();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { setLoading } = useLoading();

  const [isLoading, setIsLoading] = useState(false);
  const [selectedCoverImage, setSelectedCoverImage] = useState(null);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [productOptions, setProductOptions] = useState([]);
  const [productSearchLoading] = useState(false);

  const defaultValues = {
    name: "",
    theme_color: "",
    description_en: "",
    description_th: "",
    display_image: null,
    close_up_image: null, // close up image of product in collection
    pdf_url: "",
    product_choice_collections: [],
    // * ข้อมูลที่ใช้จริงในการเก็บ hotspot / image
    // hotspot_first_image: null,
    // hotspot_second_image: null,
    // hotspot_third_image: null,
    // hotspot_fourth_image: null,
    // hotspot_fifth_image: null,
    // hotspot_sixth_image: null,
    product_choice_hotspots: [
      // {
      //   img_ref: null,
      //   top: "",
      //   left: "",
      //   product_choice_hotspot: null,
      // },
    ],

    // ! ใช้ตอน upload file พร้อมวาง hotspot
    hotspot_images: [
      {
        ref_index: 1,
        image: null,
        hotspot: [
          // {
          //   pc_id: null,
          //   position_x: 0,
          //   position_y: 0,
          // },
        ],
      },
      {
        ref_index: 2,
        image: null,
        hotspot: [],
      },
      {
        ref_index: 3,
        image: null,
        hotspot: [],
      },
      {
        ref_index: 4,
        image: null,
        hotspot: [],
      },
      {
        ref_index: 5,
        image: null,
        hotspot: [],
      },
      {
        ref_index: 6,
        image: null,
        hotspot: [],
      },
    ],
    count_collection: 0,
  };

  const resolver = useYupValidationResolver(collectionSchema);
  const {
    handleSubmit,
    formState: { errors: formErrors, isValid },
    control: formControl,
    watch,
    trigger,
    getValues,
    setValue: setFormValue,
    reset,
  } = useForm({
    resolver,
    defaultValues,
  });
  const [formData, setFormData] = useState(defaultValues);

  const hotspotRegex = /^hotspot_images\[(\d+)\]\.hotspot\[(\d+)\]\.pc_id$/;

  const fetchCollectionDetail = async () => {
    try {
      setLoading(true);
      const data = await getCollectionById(collectionId);
      if (data) {
        const {
          hotspot_first_image,
          hotspot_second_image,
          hotspot_third_image,
          hotspot_fourth_image,
          hotspot_fifth_image,
          hotspot_sixth_image,
          ...rest
        } = data;

        const formattedData = {
          ...rest,
          // remove null values in product_choice_collections
          product_choice_collections: data.product_choice_collections.filter(
            ({ product_choice_collection }) => !!product_choice_collection,
          ),
          hotspot_images: [
            {
              ref_index: 1,
              image: hotspot_first_image || null,
              hotspot: data.product_choice_hotspots
                .filter((item) => item.img_ref === 1)
                .map((item) => ({
                  pc_id: item.product_choice_hotspot,
                  position_x: item.left,
                  position_y: item.top,
                })),
            },
            {
              ref_index: 2,
              image: hotspot_second_image || null,
              hotspot: data.product_choice_hotspots
                .filter((item) => item.img_ref === 2)
                .map((item) => ({
                  pc_id: item.product_choice_hotspot,
                  position_x: item.left,
                  position_y: item.top,
                })),
            },
            {
              ref_index: 3,
              image: hotspot_third_image || null,
              hotspot: data.product_choice_hotspots
                .filter((item) => item.img_ref === 3)
                .map((item) => ({
                  pc_id: item.product_choice_hotspot,
                  position_x: item.left,
                  position_y: item.top,
                })),
            },
            {
              ref_index: 4,
              image: hotspot_fourth_image || null,
              hotspot: data.product_choice_hotspots
                .filter((item) => item.img_ref === 4)
                .map((item) => ({
                  pc_id: item.product_choice_hotspot,
                  position_x: item.left,
                  position_y: item.top,
                })),
            },
            {
              ref_index: 5,
              image: hotspot_fifth_image || null,
              hotspot: data.product_choice_hotspots
                .filter((item) => item.img_ref === 5)
                .map((item) => ({
                  pc_id: item.product_choice_hotspot,
                  position_x: item.left,
                  position_y: item.top,
                })),
            },
            {
              ref_index: 6,
              image: hotspot_sixth_image || null,
              hotspot: data.product_choice_hotspots
                .filter((item) => item.img_ref === 6)
                .map((item) => ({
                  _id: item._id,
                  pc_id: item.product_choice_hotspot,
                  position_x: item.left,
                  position_y: item.top,
                })),
            },
          ],
        };
        reset(formattedData);
      }
    } catch (error) {
      console.error("Error fetching collection detail:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!collectionId) return;

    fetchCollectionDetail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [collectionId]);

  // fetch product options for autocomplete
  useEffect(() => {
    const fetchProducts = async () => {
      const { data } = await searchProduct({});
      setProductOptions(
        data?.rows.length > 0
          ? data?.rows?.map((product) => ({
              product_choice_collection: { ...formatFromElastic(product) },
            }))
          : [],
      );
    };
    fetchProducts();
  }, []);

  /**
   * @notice after form value change, then set value to formData state
   */
  useEffect(() => {
    const subscription = watch((values, { name, type }) => {
      setFormData(values);
    });
    return () => subscription.unsubscribe();
  }, [watch]);

  /**
   * @notice trigger validation when formData updated
   */
  useEffect(() => {
    trigger();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData]);

  const isAllowedMedia = (file) => {
    const allowedExtensions = ["png", "jpg", "jpeg"];

    if (!validFileExtensions(file, allowedExtensions)) {
      enqueueSnackbar(
        `File type is not allowed. Only accept ${allowedExtensions.join(
          ", ",
        )}.`,
        { variant: "error" },
      );
      return false;
    } else if (file.size > 2428800) {
      enqueueSnackbar(`File size is too large. Max file size is 2MB`, {
        variant: "error",
      });
      return false;
    }

    return true;
  };

  const handleChange = async (event, replaceName, replaceValue) => {
    // Helper: Check if the provided value is a valid media file
    const isValidMedia = (val) =>
      typeof val === "object" && val !== null && !isAllowedMedia(val);
    // Image key
    const imageList = ["display_image", "close_up_image"];

    let name =
      typeof replaceName !== "undefined" ? replaceName : event.target.name;
    let value =
      typeof replaceValue !== "undefined" ? replaceValue : event.target.value;

    let path = name.split(".");
    let [key, ...rest] = path;

    let editingKey = key;
    let updated;

    if (
      key === "name" ||
      key === "theme_color" ||
      key === "description_th" ||
      key === "description_en"
    ) {
      // Handle simple fields (name, theme_color, description_th, description_en)
      updated = value;
    } else if (imageList.includes(key)) {
      // Handle image fields
      if (isValidMedia(value)) return;
      updated = value;
    } else if (key === "product_choice_collections") {
      updated = value;
    } else if (key === "hotspot_images") {
      // Handle hotspot_image fields
      let [objIndex, objKey, subObjIndex] = rest;
      editingKey = `${key}.${objIndex}`;
      let editingRow = getValues(editingKey);

      if (objKey === "image" && value === null) {
        // Remove image and its hotspots
        updated = { ...editingRow, image: null, hotspot: [] };
      } else if (objKey === "image") {
        // Handle image fields
        if (isValidMedia(value)) return;
        // Update image for a paragraph
        updated = { ...editingRow, image: value };
      } else if (objKey === "hotspot") {
        // Update a specific hotspot in hotspot_image
        updated = {
          ...editingRow,
          hotspot: editingRow.hotspot.map((spot, spotIndex) =>
            spotIndex === parseInt(subObjIndex)
              ? { ...spot, pc_id: value }
              : spot,
          ),
        };
      }
    } else {
      // Default: set value if key is not recognized
      updated = value;
    }
    setFormValue(editingKey, updated);
  };

  const handleBack = () => {
    navigate(-1);
  };

  const handleRemoveProduct = (productId) => {
    setSelectedProducts(selectedProducts.filter((p) => p.id !== productId));
  };

  const onSubmit = async (submitData) => {
    try {
      setIsLoading(true);

      // Create FormData for file upload
      const formData = new FormData();

      const { hotspot_images, ...data } = submitData;

      const createParams = {
        ...data,
        product_choice_hotspots: [],
        product_choice_collections: data.product_choice_collections.map(
          (product) => {
            if (!!collectionId) {
              product._id =
                product._id ?? `new-${product.product_choice_collection?._id}`;
            }
            product.product_choice_collection = {
              _id: product.product_choice_collection?._id,
            };
            return product;
          },
        ),
      };

      // format hotspot_images data to product_choice_hotspots structure
      hotspot_images.forEach((hotspotImage, index) => {
        // map hotspot images to respective fields
        switch (hotspotImage.ref_index) {
          case 1:
            createParams.hotspot_first_image = hotspotImage.image;
            break;
          case 2:
            createParams.hotspot_second_image = hotspotImage.image;
            break;
          case 3:
            createParams.hotspot_third_image = hotspotImage.image;
            break;
          case 4:
            createParams.hotspot_fourth_image = hotspotImage.image;
            break;
          case 5:
            createParams.hotspot_fifth_image = hotspotImage.image;
            break;
          case 6:
            createParams.hotspot_sixth_image = hotspotImage.image;
            break;
          default:
            break;
        }

        if (hotspotImage.hotspot.length > 0) {
          hotspotImage.hotspot.forEach((spot) => {
            createParams.product_choice_hotspots.push({
              ...(!!collectionId
                ? { _id: !!spot?._id ? spot?._id : `new-${spot?.pc_id?._id}` }
                : {}),
              img_ref: hotspotImage.ref_index,
              left: spot.position_x,
              top: spot.position_y,
              product_choice_hotspot: { _id: spot?.pc_id?._id },
            });
          });
        }
      });

      Object.keys(createParams).forEach((key) => {
        if (
          key === "product_choice_collections" ||
          key === "product_choice_hotspots"
        ) {
          formData.append(key, JSON.stringify(createParams[key]));
        } else if (key === "pdf_url" && checkNotEmpty(createParams.pdf_url)) {
          formData.append(key, createParams[key]);
        } else if (Array.isArray(createParams[key])) {
          formData.append(key, JSON.stringify(createParams[key]));
        } else {
          formData.append(key, createParams[key]);
        }
        return formData;
      });

      !!collectionId
        ? await updateCollection(formData)
        : await createCollection(formData);

      enqueueSnackbar(`${!!collectionId ? "แก้ไข" : "สร้าง"}คอลเล็กชันสำเร็จ`, {
        variant: "success",
      });
      navigate("/collection/list");
    } catch (error) {
      console.error("Error creating collection:", error);

      const errorMessage =
        error?.response?.data?.message || "เกิดข้อผิดพลาดในการสร้างคอลเล็กชัน";
      enqueueSnackbar(errorMessage, { variant: "error" });
    } finally {
      setIsLoading(false);
    }
  };

  const renderImageUploadBox = (index) => (
    <Box key={index} sx={{ position: "relative" }}>
      {formData?.hotspot_images[index]?.image ? (
        <>
          <SpottableImage
            formControl={formControl}
            aspectRatio={[0, 3, 4]?.includes(index) ? 18.75 / 9 : 1}
            previewImage={
              typeof formData.hotspot_images[index].image === "string"
                ? formData.hotspot_images[index].image
                : URL.createObjectURL(formData.hotspot_images[index].image)
            }
            hotspot={formData.hotspot_images[index].hotspot}
            hotspotArrayKey={`hotspot_images.${index}.hotspot`}
            mode={"edit"}
            productOptions={formData.product_choice_collections.map(
              ({ product_choice_collection: product }) => product,
            )}
            onSelectProduct={(value, spotIndex) => {
              handleChange(
                null,
                `hotspot_images.${index}.hotspot.${spotIndex}`,
                value,
              );
            }}
          />

          <RemoveButton
            onClick={() => {
              handleChange(null, `hotspot_images.${index}.image`, null);
            }}
          >
            <CloseIcon />
          </RemoveButton>
        </>
      ) : (
        <>
          <UploadBox
            aspectRatio={[0, 3, 4]?.includes(index) ? 18.75 / 9 : 1}
            onClick={() =>
              document.getElementById(`hotspot_images.${index}.image`).click()
            }
          >
            <AddPhotoAlternateIcon sx={{ fontSize: 48, color: Grey, mb: 1 }} />
            <UploadText>เพิ่มรูปภาพ</UploadText>
            <UploadHelper>รองรับไฟล์ JPG, PNG</UploadHelper>
            <input
              id={`hotspot_images.${index}.image`}
              type="file"
              accept=".jpg,.jpeg,.png"
              style={{ display: "none" }}
              onChange={(e) => {
                if (e.target.files[0]) {
                  e.preventDefault();
                  handleChange(
                    null,
                    `hotspot_images.${index}.image`,
                    e.target.files[0],
                  );
                }
              }}
              onClick={(e) => {
                e.currentTarget.value = null;
              }}
            />
          </UploadBox>
        </>
      )}
    </Box>
  );

  return (
    <Main open={open}>
      {isLoading && <Loading />}

      <Container>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Grid container spacing={3}>
            {/* Cover Image Upload */}
            <Grid item xs={12}>
              <DivBackground>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <SectionTitle>
                      รูปปกคอลเล็กชัน <span>*</span>
                    </SectionTitle>
                  </Grid>

                  <Grid item xs={12}>
                    {formData.display_image ? (
                      <UploadedPreview>
                        <CoverPreviewImage
                          src={
                            typeof formData.display_image === "string"
                              ? formData.display_image
                              : URL.createObjectURL(formData.display_image)
                          }
                          alt="Cover"
                        />

                        <RemoveButton
                          onClick={() => {
                            handleChange(null, "display_image", null);
                          }}
                        >
                          <CloseIcon />
                        </RemoveButton>
                      </UploadedPreview>
                    ) : (
                      <>
                        <UploadImage
                          callBackFile={(fileCallBlack) => {
                            fileCallBlack !== "" &&
                              handleChange(
                                null,
                                "display_image",
                                fileCallBlack,
                              );
                          }}
                          closeAlert={false}
                        >
                          <UploadBox aspectRatio={1440 / 540}>
                            <AddPhotoAlternateIcon
                              sx={{
                                fontSize: 48,
                                color: Grey,
                                mb: 1,
                              }}
                            />
                            <UploadText>
                              ลากและวางไฟล์รูปภาพที่นี่ หรือคลิกเพื่อเลือกไฟล์
                            </UploadText>

                            <UploadHelper>
                              {/* ขนาดที่แนะนำ: 930x400 พิกเซล
                              <br /> */}
                              รองรับไฟล์: JPG, PNG เท่านั้น
                              <br />
                              ขนาดไฟล์ไม่เกิน 10MB
                            </UploadHelper>
                          </UploadBox>
                        </UploadImage>

                        <FormHelperText sx={{ color: "error.main" }}>
                          {formErrors.display_image?.message}
                        </FormHelperText>
                      </>
                    )}
                  </Grid>
                </Grid>
              </DivBackground>
            </Grid>

            {/* About Collection */}
            <Grid item xs={12}>
              <DivBackground>
                <Grid
                  container
                  spacing={3}
                  sx={{
                    ".MuiFormHelperText-root": {
                      position: "absolute",
                      bottom: "-20px",
                    },
                  }}
                >
                  <Grid item xs={12}>
                    <SectionTitle>
                      เกี่ยวกับคอลเล็กชัน <span>*</span>
                    </SectionTitle>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="ชื่อคอลเล็กชัน"
                      value={formData.name}
                      onChange={(e) => handleChange(e, `name`)}
                      error={!!formErrors.name}
                      helperText={formErrors.name?.message}
                      placeholder="เช่น Modern Living Collection"
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="ธีมสี"
                      InputProps={{
                        endAdornment: (
                          <Box
                            sx={{
                              backgroundColor:
                                watch("theme_color") || "#ffffff",
                              border: "1px solid #ccc",
                              borderRadius: "50%",
                              width: 26,
                              height: 24,
                            }}
                          />
                        ),
                      }}
                      value={formData.theme_color}
                      onChange={(e) => handleChange(e, `theme_color`)}
                      error={!!formErrors.theme_color}
                      helperText={formErrors.theme_color?.message}
                      placeholder="เช่น #FF5733"
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      multiline
                      rows={4}
                      label="รายละเอียดคอลเล็กชัน (ภาษาไทย)"
                      value={formData.description_th}
                      onChange={(e) => handleChange(e, `description_th`)}
                      error={!!formErrors.description_th}
                      helperText={formErrors.description_th?.message}
                      placeholder="อธิบายคอนเซ็ปต์และสไตล์ของคอลเล็กชันเป็นภาษาไทย"
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      multiline
                      rows={4}
                      label="รายละเอียดคอลเล็กชัน (ภาษาอังกฤษ)"
                      value={formData.description_en}
                      onChange={(e) => handleChange(e, `description_en`)}
                      error={!!formErrors.description_en}
                      helperText={formErrors.description_en?.message}
                      placeholder="Describe the concept and style of the collection in English"
                    />
                  </Grid>
                </Grid>
              </DivBackground>
            </Grid>

            {/* Product Selection */}
            <Grid item xs={12}>
              <DivBackground>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <SectionTitle>
                      เลือกสินค้าในคอลเล็กชัน <span>*</span>
                    </SectionTitle>
                  </Grid>

                  <Grid item xs={12}>
                    <Autocomplete
                      multiple
                      options={productOptions}
                      getOptionLabel={(option) =>
                        `${option?.product_choice_collection?.product?.item_name} (${option.product_choice_collection?.product?.item_code})`
                      }
                      getOptionSelected={(option, value) =>
                        option?.product_choice_collection?._id ===
                        value?.product_choice_collection?._id
                      }
                      isOptionEqualToValue={(option, value) =>
                        option?.product_choice_collection?._id ===
                        value?.product_choice_collection?._id
                      }
                      value={formData.product_choice_collections}
                      onChange={(e, newValue) =>
                        handleChange(
                          null,
                          "product_choice_collections",
                          newValue,
                        )
                      }
                      loading={productSearchLoading}
                      renderOption={(props, option) => (
                        <Box
                          component="li"
                          {...props}
                          key={`item-${option?.product_choice_collection._id}`}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 2,
                            p: 2,
                          }}
                        >
                          {!!option?.product_choice_collection?.group_image
                            ?.product_display_image === false ? (
                            <ImageEmpty>
                              <InsertPhotoIcon
                                sx={{
                                  color: "rgba(0, 0, 0, 0.54)",
                                  cursor: "pointer",
                                }}
                              />
                            </ImageEmpty>
                          ) : (
                            <img
                              style={{
                                width: "80px",
                                height: "80px",
                                objectFit: "cover",
                                objectPosition: "center",
                                borderRadius: "12px",
                                marginRight: "8px",
                              }}
                              src={
                                option?.product_choice_collection?.group_image
                                  ?.product_display_image
                                  ? option?.product_choice_collection?.group_image?.product_display_image.replace(
                                      ".com",
                                      ".com/300x300",
                                    )
                                  : ""
                              }
                              alt={
                                option?.product_choice_collection?.group_image
                                  ?.product_display_image
                              }
                            />
                          )}
                          <ProductChoiceDetail>
                            {/* name */}
                            <SpanProductDetail nameProd>
                              {option?.product_choice_collection?.product
                                ?.item_name
                                ? option?.product_choice_collection?.product
                                    ?.item_name
                                : "ไม่มีชื่อ"}
                            </SpanProductDetail>

                            {/* size */}
                            <SpanProductDetail center>
                              {option?.product_choice_collection?.size
                                ?.quantity === "size"
                                ? `${option?.product_choice_collection?.size?.width} ${
                                    getLocale() === "th"
                                      ? option?.product_choice_collection?.size
                                          ?.unit_width?.unit_th
                                      : option?.product_choice_collection?.size
                                          ?.unit_width?.unit_en
                                  } x ${option?.product_choice_collection?.size?.height} ${
                                    getLocale() === "th"
                                      ? option?.product_choice_collection?.size
                                          ?.unit_height?.unit_th
                                      : option?.product_choice_collection?.size
                                          ?.unit_height?.unit_en
                                  } x ${option?.product_choice_collection?.size?.thickness} ${
                                    getLocale() === "th"
                                      ? option?.product_choice_collection?.size
                                          ?.unit_thickness?.unit_th
                                      : option?.product_choice_collection?.size
                                          ?.unit_thickness?.unit_en
                                  }`
                                : option?.product_choice_collection?.size
                                      ?.quantity === "capacity"
                                  ? `${option?.product_choice_collection?.size?.capacity_size} ${
                                      getLocale() === "th"
                                        ? option?.product_choice_collection
                                            ?.size?.unit_capacity?.unit_th
                                        : option?.product_choice_collection
                                            ?.size?.unit_capacity?.unit_en
                                    }`
                                  : getLocale() === "th"
                                    ? "สินค้าสั่งทำพิเศษ"
                                    : "Made by order"}
                            </SpanProductDetail>

                            {/* item_code */}
                            <SpanProductDetail>
                              {option?.product_choice_collection?.product
                                ?.item_code
                                ? option?.product_choice_collection?.product
                                    ?.item_code
                                : "-"}
                            </SpanProductDetail>

                            {/* choice name */}
                            <SpanProductDetail center>
                              {option?.product_choice_collection?.name !== ""
                                ? `( ${option?.product_choice_collection?.name} )`
                                : `-`}
                            </SpanProductDetail>

                            {/* price */}
                            <SpanProductDetail price>
                              {convertPrice(
                                option?.product_choice_collection?.group_price
                                  ?.is_start_price === true
                                  ? option?.product_choice_collection
                                      ?.group_price?.start_price
                                  : option?.product_choice_collection
                                        ?.group_price.special_price > 0 &&
                                      option?.product_choice_collection
                                        ?.group_price.special_price !== null &&
                                      option?.product_choice_collection
                                        ?.group_price.special_price !==
                                        undefined
                                    ? option?.product_choice_collection
                                        ?.group_price.special_price
                                    : option?.product_choice_collection
                                        ?.group_price?.price,
                              )}
                            </SpanProductDetail>
                          </ProductChoiceDetail>
                        </Box>
                      )}
                      filterOptions={createFilterOptions({
                        matchFrom: "any",
                        limit: 100,
                      })}
                      sx={{
                        ".MuiOutlinedInput-root .MuiAutocomplete-input": {
                          padding: "3px 4px 3px 6px",
                        },
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="ค้นหาและเลือกสินค้า"
                          placeholder="พิมพ์ชื่อสินค้าหรือรหัสสินค้า"
                          InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                              <>
                                {productSearchLoading && <Loading size={20} />}
                                {params.InputProps.endAdornment}
                              </>
                            ),
                          }}
                        />
                      )}
                    />
                  </Grid>

                  {/* Selected Products Display */}
                  {formData.product_choice_collections.length > 0 && (
                    <>
                      <Grid item xs={12} sx={{ mt: 2 }}>
                        <Typography variant="subtitle2">
                          สินค้าที่เลือก (
                          {formData.product_choice_collections.length} รายการ)
                        </Typography>
                      </Grid>

                      <Grid item xs={12} container spacing={2}>
                        {formData.product_choice_collections.map(
                          ({ product_choice_collection: product }) => (
                            <Grid item xs={12} sm={6} md={4} key={product?._id}>
                              <ProductCard>
                                <CardContent>
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "flex-start",
                                      gap: 2,
                                    }}
                                  >
                                    {!!product?.group_image
                                      ?.product_display_image === false ? (
                                      <ImageEmpty>
                                        <InsertPhotoIcon
                                          sx={{
                                            color: "rgba(0, 0, 0, 0.54)",
                                            cursor: "pointer",
                                          }}
                                        />
                                      </ImageEmpty>
                                    ) : (
                                      <img
                                        style={{
                                          width: "80px",
                                          height: "80px",
                                          objectFit: "cover",
                                          objectPosition: "center",
                                          borderRadius: "12px",
                                          marginRight: "8px",
                                        }}
                                        src={
                                          product?.group_image
                                            ?.product_display_image
                                            ? product?.group_image?.product_display_image.replace(
                                                ".com",
                                                ".com/300x300",
                                              )
                                            : ""
                                        }
                                        alt={
                                          product?.group_image
                                            ?.product_display_image
                                        }
                                      />
                                    )}

                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                      <Typography variant="subtitle2" noWrap>
                                        {product?.product?.item_name}
                                      </Typography>

                                      <Typography
                                        variant="body2"
                                        color="textSecondary"
                                      >
                                        {product?.product?.item_code}
                                      </Typography>

                                      <Typography
                                        variant="body2"
                                        color={Primary}
                                      >
                                        {convertPrice(
                                          product?.group_price?.price,
                                        )}
                                      </Typography>
                                    </Box>

                                    <IconButton
                                      size="small"
                                      onClick={() =>
                                        handleRemoveProduct(product._id)
                                      }
                                      sx={{ color: "#e04132" }}
                                    >
                                      <DeleteOutlineIcon fontSize="small" />
                                    </IconButton>
                                  </Box>
                                </CardContent>
                              </ProductCard>
                            </Grid>
                          ),
                        )}
                      </Grid>
                    </>
                  )}
                </Grid>
              </DivBackground>
            </Grid>

            {/* Additional Images */}
            <Grid item xs={12}>
              <DivBackground>
                <Grid container columnSpacing={3} rowSpacing={3}>
                  <Grid item xs={12}>
                    <SectionTitle>
                      รูปภาพ Close up ของสินค้าที่ต้องการแสดงในหน้ารวมคอลเล็กชัน{" "}
                      <span>*</span>
                    </SectionTitle>
                  </Grid>

                  <Grid item xs={4}>
                    {formData.close_up_image ? (
                      <UploadedPreview>
                        <PreviewImage
                          src={
                            typeof formData.close_up_image === "string"
                              ? formData.close_up_image
                              : URL.createObjectURL(formData.close_up_image)
                          }
                          alt="Close up"
                        />

                        <RemoveButton
                          onClick={() => {
                            handleChange(null, "close_up_image", null);
                          }}
                        >
                          <CloseIcon />
                        </RemoveButton>
                      </UploadedPreview>
                    ) : (
                      <UploadImage
                        callBackFile={(fileCallBlack) => {
                          fileCallBlack !== "" &&
                            handleChange(null, "close_up_image", fileCallBlack);
                        }}
                        closeAlert={false}
                      >
                        <UploadBox aspectRatio={1}>
                          <AddPhotoAlternateIcon
                            sx={{
                              fontSize: 48,
                              color: Grey,
                              mb: 1,
                            }}
                          />
                          <UploadText>
                            อัปโหลดรูปภาพ/เลือกสินค้าที่ต้องการเพิ่มรูปภาพ Close
                            up
                          </UploadText>
                        </UploadBox>
                      </UploadImage>
                    )}
                  </Grid>

                  <Grid item xs={8}>
                    <Autocomplete
                      options={formData.product_choice_collections}
                      getOptionLabel={(option) =>
                        `${option?.product_choice_collection?.product?.item_name} (${option.product_choice_collection?.product?.item_code})`
                      }
                      getOptionSelected={(option, value) =>
                        option?.product_choice_collection?._id ===
                        value?.product_choice_collection?._id
                      }
                      isOptionEqualToValue={(option, value) =>
                        option?.product_choice_collection?._id ===
                        value?.product_choice_collection?._id
                      }
                      value={selectedCoverImage}
                      onChange={(e, newValue) => {
                        const newImage = !!newValue.product_choice_collection
                          ?.group_image?.product_closeup_image
                          ? newValue.product_choice_collection?.group_image
                              ?.product_closeup_image
                          : newValue.product_choice_collection?.group_image
                              ?.product_full_image;
                        handleChange(null, "close_up_image", newImage);
                        setSelectedCoverImage(newValue);
                      }}
                      loading={productSearchLoading}
                      renderOption={(props, option) => (
                        <Box
                          component="li"
                          {...props}
                          key={`item-${option?.product_choice_collection._id}`}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 2,
                            p: 2,
                          }}
                        >
                          {!option?.product_choice_collection?.group_image
                            ?.product_closeup_image &&
                          !option?.product_choice_collection?.group_image
                            ?.product_full_image ? (
                            <ImageEmpty>
                              <InsertPhotoIcon
                                sx={{
                                  color: "rgba(0, 0, 0, 0.54)",
                                  cursor: "pointer",
                                }}
                              />
                            </ImageEmpty>
                          ) : (
                            <img
                              style={{
                                width: "80px",
                                height: "80px",
                                objectFit: "cover",
                                objectPosition: "center",
                                borderRadius: "12px",
                                marginRight: "8px",
                              }}
                              src={
                                option?.product_choice_collection?.group_image
                                  ?.product_closeup_image
                                  ? option?.product_choice_collection?.group_image?.product_closeup_image.replace(
                                      ".com",
                                      ".com/300x300",
                                    )
                                  : option?.product_choice_collection
                                        ?.group_image?.product_full_image
                                    ? option?.product_choice_collection?.group_image?.product_full_image.replace(
                                        ".com",
                                        ".com/300x300",
                                      )
                                    : ""
                              }
                              alt={
                                option?.product_choice_collection?.group_image
                                  ?.product_closeup_image ||
                                option?.product_choice_collection?.group_image
                                  ?.product_full_image
                              }
                            />
                          )}
                          <Box
                            sx={{ display: "flex", flexDirection: "column" }}
                          >
                            {/* name */}
                            <SpanProductDetail nameProd>
                              {option?.product_choice_collection?.product
                                ?.item_name
                                ? option?.product_choice_collection?.product
                                    ?.item_name
                                : "ไม่มีชื่อ"}
                            </SpanProductDetail>

                            {/* item_code */}
                            <SpanProductDetail>
                              {option?.product_choice_collection?.product
                                ?.item_code
                                ? option?.product_choice_collection?.product
                                    ?.item_code
                                : "-"}
                            </SpanProductDetail>
                          </Box>
                        </Box>
                      )}
                      filterOptions={createFilterOptions({
                        matchFrom: "any",
                        limit: 100,
                      })}
                      sx={{
                        ".MuiOutlinedInput-root .MuiAutocomplete-input": {
                          padding: "3px 4px 3px 6px",
                        },
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="เลือกสินค้าเพื่อเพิ่มรูปภาพ Close up"
                          placeholder="พิมพ์ชื่อสินค้าหรือรหัสสินค้า"
                        />
                      )}
                    />

                    {!!formErrors.close_up_image?.message && (
                      <FormHelperText sx={{ color: "error.main" }}>
                        {formErrors.close_up_image?.message}
                      </FormHelperText>
                    )}
                  </Grid>

                  <Grid item xs={12}>
                    <SectionTitle>
                      รูปภาพในคอลเล็กชันเพิ่มเติม <span>*</span>
                    </SectionTitle>

                    {!!formErrors.hotspot_images?.message && (
                      <FormHelperText sx={{ color: "error.main" }}>
                        {formErrors.hotspot_images?.message}
                      </FormHelperText>
                    )}
                    {Object.keys(formErrors).some((key) =>
                      hotspotRegex.test(key),
                    ) && (
                      <FormHelperText sx={{ color: "error.main" }}>
                        จุด hotspot ต้องไม่ว่างเปล่า
                      </FormHelperText>
                    )}
                  </Grid>

                  {Array.from({ length: 6 }, (_, index) => (
                    <Grid
                      item
                      xs={[0, 3, 4].includes(index) ? 8 : 4}
                      key={index}
                    >
                      {renderImageUploadBox(index)}
                    </Grid>
                  ))}
                </Grid>
              </DivBackground>
            </Grid>

            {/* PDF Upload */}
            <Grid item xs={12}>
              <DivBackground>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <SectionTitle>
                      เอกสาร PDF คอลเล็กชัน (ไม่บังคับ)
                    </SectionTitle>
                  </Grid>

                  <Grid item xs={12} container spacing={2}>
                    <Grid item xs={12}>
                      <TextField
                        variant="outlined"
                        fullWidth
                        InputProps={{
                          readOnly: true,
                          endAdornment: (
                            <>
                              {checkNotEmpty(formData.pdf_url) ? (
                                <IconButton
                                  // sx={{ padding: 0 }}
                                  onClick={() => {
                                    handleChange(null, "pdf_url", null);
                                  }}
                                >
                                  <DeleteOutlineIcon
                                    sx={{
                                      color: Primary,
                                      cursor: "pointer",
                                    }}
                                  />
                                </IconButton>
                              ) : (
                                <UploadPdf
                                  callBackFile={(fileCallBack) =>
                                    handleChange(null, "pdf_url", fileCallBack)
                                  }
                                  closeAlert={false}
                                >
                                  <IconButton>
                                    <PictureAsPdfIcon
                                      sx={{
                                        color: Black,
                                        cursor: "pointer",
                                        ":hover": {
                                          color: "#e04132",
                                        },
                                      }}
                                    />
                                  </IconButton>
                                </UploadPdf>
                              )}
                            </>
                          ),
                        }}
                        InputLabelProps={{ shrink: true }}
                        placeholder=".pdf เท่านั้น, ขนาดไม่เกิน 10MB"
                        value={
                          !!formData?.pdf_url
                            ? typeof formData?.pdf_url === "string"
                              ? formData?.pdf_url
                              : !!formData?.pdf_url?.name
                                ? formData?.pdf_url?.name
                                : formData?.pdf_url?.originalFileName
                            : ""
                        }
                        error={!!formErrors?.pdf_url}
                      />
                    </Grid>
                  </Grid>
                </Grid>
              </DivBackground>
            </Grid>
          </Grid>

          {/* Submit Buttons */}
          <ButtonGroup
            flexDirection={"row"}
            justifyContent={"center"}
            alignItems={"center"}
            columnGap={2}
          >
            <Button
              variant="secondary"
              size="large"
              onClick={handleBack}
              sx={{ minWidth: 120 }}
            >
              ยกเลิก
            </Button>
            <Button
              type="submit"
              variant="contained"
              size="large"
              startIcon={<SaveIcon />}
              disabled={isLoading || !isValid}
              sx={{ minWidth: 180 }}
            >
              {isLoading
                ? "กำลังสร้าง..."
                : !!collectionId
                  ? "แก้ไขคอลเล็กชัน"
                  : "สร้างคอลเล็กชัน"}
            </Button>
          </ButtonGroup>
        </form>
      </Container>
    </Main>
  );
};

export default CollectionForm;
